import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../userContext';

function GetStartedWizard() {
  const { userProfile, campaigns } = useUser();

  const hasCampaigns = campaigns && campaigns.length > 0;
  const hasLiveCampaign = campaigns?.some(campaign => campaign.status === 'live');
  // For now, we'll consider embed step complete if they have a live campaign
  // In the future, this could track actual embed usage or integration
  const hasEmbeddedCampaign = hasLiveCampaign;

  // Determine which step is active
  const isCreateCampaignActive = !hasCampaigns;
  const isLaunchActive = hasCampaigns && !hasLiveCampaign;
  const isEmbedActive = hasLiveCampaign && !hasEmbeddedCampaign;

  // Helper function to determine step status
  const getStepStyles = (isComplete, isActive) => {
    if (isActive) return {
      container: 'transition-all duration-300',
      containerStyle: {
        background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
        border: '2px solid #E67E22',
        boxShadow: '0 4px 12px rgba(241, 196, 15, 0.4)'
      },
      icon: '#2C3E50',
      text: '#F1C40F',
      action: '#ECF0F1'
    };
    if (isComplete) return {
      container: 'transition-all duration-300',
      containerStyle: {
        background: 'linear-gradient(135deg, #27AE60, #2ECC71)',
        border: '2px solid #F1C40F',
        boxShadow: '0 4px 12px rgba(39, 174, 96, 0.4)'
      },
      icon: '#FFFFFF',
      text: '#F1C40F',
      action: '#ECF0F1'
    };
    return {
      container: 'transition-all duration-300',
      containerStyle: {
        background: 'rgba(52, 73, 94, 0.6)',
        border: '1px solid rgba(149, 165, 166, 0.3)'
      },
      icon: '#95A5A6',
      text: '#95A5A6',
      action: '#95A5A6'
    };
  };

  // Determine which step is active (now 3 steps)
  const step1Styles = getStepStyles(hasCampaigns, !hasCampaigns);
  const step2Styles = getStepStyles(hasLiveCampaign, hasCampaigns && !hasLiveCampaign);
  const step3Styles = getStepStyles(hasEmbeddedCampaign, hasLiveCampaign && !hasEmbeddedCampaign);

  if (hasEmbeddedCampaign) return null;

  return (
    <div className="mb-8 studio-card rounded-xl" style={{fontFamily: '"Share Tech Mono", monospace'}}>
      {/* Welcome Banner */}
      <div className="px-6 py-8 border-b rounded-t-xl" style={{
        borderColor: 'rgba(241, 196, 15, 0.3)',
        background: 'linear-gradient(135deg, rgba(52, 73, 94, 0.8), rgba(44, 62, 80, 0.8))'
      }}>
        <h2 className="text-2xl font-black mb-2 tracking-wider" style={{
          color: '#F1C40F',
          textShadow: '2px 2px 0px #E67E22'
        }}>
          🚀 WELCOME TO STUDIO, {userProfile.firstName.toUpperCase()}!
        </h2>
        <p className="font-bold" style={{color: '#ECF0F1'}}>
          Get started with interactive campaigns that drive engagement and generate leads for your business.
        </p>
      </div>

      {/* Steps */}
      <div className="px-6 py-6">
        <div className="flex flex-col md:flex-row items-stretch gap-6 md:gap-4 justify-between">
          {/* Step 1: Create Campaign */}
          <div className="flex items-center gap-4 w-full md:w-1/3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${step1Styles.container}`} style={step1Styles.containerStyle}>
              <div className="text-2xl">📊</div>
            </div>
            <div className="flex flex-col flex-grow">
              <h3 className="text-sm font-black tracking-wider" style={{color: step1Styles.text}}>
                STEP 1: CREATE CAMPAIGN
              </h3>
              <Link
                to="/studio/new"
                onClick={(e) => {
                  if (!isCreateCampaignActive) {
                    e.preventDefault();
                    return;
                  }
                }}
                className={`text-sm font-bold transition-all duration-100 ${!isCreateCampaignActive && !hasCampaigns ? 'cursor-not-allowed' : ''}`}
                style={{color: step1Styles.action}}
              >
                {hasCampaigns ? '✅ CAMPAIGN CREATED' : 'CREATE YOUR FIRST CAMPAIGN →'}
              </Link>
            </div>
          </div>

          {/* Step 2: Launch Campaign */}
          <div className="flex items-center gap-4 w-full md:w-1/3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${step2Styles.container}`} style={step2Styles.containerStyle}>
              <div className="text-2xl">🎯</div>
            </div>
            <div className="flex flex-col flex-grow">
              <h3 className="text-sm font-black tracking-wider" style={{color: step2Styles.text}}>
                STEP 2: LAUNCH CAMPAIGN
              </h3>
              {hasLiveCampaign ? (
                <span className="text-sm font-bold" style={{color: step2Styles.action}}>
                  ✅ CAMPAIGN LIVE
                </span>
              ) : (
                <Link 
                  to="/studio/campaigns"
                  className={`text-sm font-bold transition-all duration-100 ${!hasCampaigns ? 'cursor-not-allowed' : ''}`}
                  style={{color: step2Styles.action}}
                  onClick={(e) => {
                    if (!hasCampaigns) {
                      e.preventDefault();
                      return;
                    }
                  }}
                >
                  PUBLISH YOUR CAMPAIGN →
                </Link>
              )}
            </div>
          </div>

          {/* Step 3: Embed & Deploy */}
          <div className="flex items-center gap-4 w-full md:w-1/3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${step3Styles.container}`} style={step3Styles.containerStyle}>
              <div className="text-2xl">🌐</div>
            </div>
            <div className="flex flex-col flex-grow">
              <h3 className="text-sm font-black tracking-wider" style={{color: step3Styles.text}}>
                STEP 3: EMBED & SHARE
              </h3>
              {hasEmbeddedCampaign ? (
                <span className="text-sm font-bold" style={{color: step3Styles.action}}>
                  ✅ CAMPAIGN DEPLOYED
                </span>
              ) : (
                <Link 
                  to="/studio/campaigns"
                  className={`text-sm font-bold transition-all duration-100 ${!hasLiveCampaign ? 'cursor-not-allowed' : ''}`}
                  style={{color: step3Styles.action}}
                  onClick={(e) => {
                    if (!hasLiveCampaign) {
                      e.preventDefault();
                      return;
                    }
                  }}
                >
                  GET EMBED CODE & SHARE →
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GetStartedWizard; 